import axios, { AxiosError, type AxiosInstance } from "axios";
import { env } from "./env";

type PendingRequest = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 3;
let pendingRequests: PendingRequest[] = [];
// Set once the session is known to be over (refresh failed or the circuit
// tripped). Until the next login, every non-auth request is refused here so
// pollers still mounted for a moment don't keep hitting the API — and its
// rate limits — while the page heads to /login.
let sessionEnded = false;

const processPendingRequests = (token: string | null, error: unknown = null) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  pendingRequests = [];
};

export const api: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  if (sessionEnded && !isAuthUrl(config.url)) {
    return Promise.reject(new axios.CanceledError("Session ended"));
  }
  const tenantId = localStorage.getItem("tenantId");
  if (tenantId) {
    config.headers["X-Tenant-ID"] = tenantId;
  }
  return config;
});

const SKIP_REFRESH = ["/auth/login/", "/auth/register/", "/auth/refresh/"];

function isAuthUrl(url: string | undefined) {
  return !!url && url.includes("/auth/");
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (typeof error.config & { _retry?: boolean })
      | undefined;

    const isAuthEndpoint = SKIP_REFRESH.some((u) =>
      originalRequest?.url?.includes(u),
    );

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
        localStorage.removeItem("tenantId");
        processPendingRequests(null, error);
        forceLogoutRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshAttempts++;

      try {
        await axios.post(
          `${env.apiBaseUrl}/auth/refresh/`,
          {},
          { withCredentials: true },
        );
        refreshAttempts = 0;
        isRefreshing = false;
        processPendingRequests("ok");
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processPendingRequests(null, refreshError);
        localStorage.removeItem("tenantId");
        forceLogoutRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

function forceLogoutRedirect() {
  sessionEnded = true;
  const path = window.location.pathname;
  if (!path.startsWith("/login") && !path.startsWith("/register")) {
    window.location.href = "/login";
  }
}

export function resetRefreshCircuit() {
  refreshAttempts = 0;
  sessionEnded = false;
}
